import React, { useCallback, useEffect, useState } from "react";
import "./style.css";
import Section from "../../assets/img/Selection.png";
import NeuralNetCanvas from "../NeuralNetCanvas";
import { useNavigate } from "react-router-dom";
import useCountUp from "../../hooks/useCountUp";

/**
 * Words cycled through the headline. All are rendered invisibly inside the
 * slot so it reserves the width of the longest one — without that the line
 * reflows on every swap and the whole headline jumps.
 */
const HEADLINE_WORDS = ["Solves Real", "Automates", "Scales With"];

const WORD_INTERVAL = 2800;

export default function HeroSection() {
  const navigate = useNavigate();
  const [wordIndex, setWordIndex] = useState(0);

  // Hero is above the fold, so the count runs on mount rather than on scroll.
  const founderCount = useCountUp("50+", true, { delay: 600 });

  /**
   * Magnetic hover — the button drifts a few px toward the cursor. Offsets
   * are written to CSS custom properties so the transform stays in CSS.
   */
  const onMagneticMove = useCallback((e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty("--magnet-x", `${x * 12}px`);
    el.style.setProperty("--magnet-y", `${y * 8}px`);
  }, []);

  const onMagneticLeave = useCallback((e) => {
    const el = e.currentTarget;
    el.style.setProperty("--magnet-x", "0px");
    el.style.setProperty("--magnet-y", "0px");
  }, []);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Hold on the first word rather than cycling.
    if (prefersReducedMotion || HEADLINE_WORDS.length < 2) return;

    const id = setInterval(
      () => setWordIndex((i) => (i + 1) % HEADLINE_WORDS.length),
      WORD_INTERVAL
    );

    return () => clearInterval(id);
  }, []);

  return (
    <section className="hero-section">
      <NeuralNetCanvas />
      {/* AI background canvas — decorative only */}
      <div className="hero-bg-orb hero-bg-orb--1" aria-hidden="true" />
      <div className="hero-bg-orb hero-bg-orb--2" aria-hidden="true" />
      <div className="hero-bg-orb hero-bg-orb--3" aria-hidden="true" />

      <div className="section-wrapper">
        <div className="container">
          <div className="row align-items-center hero-row">
            {/* Left Column: Text Content */}
            <div className="col-lg-6 col-12">
              <div className="hero-content">
                {/* H1 Headline */}
                <h1
                  className="hero-headline animate-in"
                  style={{ "--enter-delay": "80ms" }}
                >
                  AI-Powered Software That{" "}
                  <span className="hero-word-slot">
                    {/* Sizes the slot to the longest word; never shown. */}
                    <span className="hero-word-sizer" aria-hidden="true">
                      {HEADLINE_WORDS.reduce((a, b) =>
                        b.length > a.length ? b : a
                      )}
                    </span>
                    {HEADLINE_WORDS.map((word, i) => (
                      <span
                        key={word}
                        className={`hero-word text-gradient ${
                          i === wordIndex ? "is-active" : ""
                        }`}
                        aria-hidden={i === wordIndex ? undefined : true}
                      >
                        {word}
                      </span>
                    ))}
                  </span>{" "}
                  Business Problems
                </h1>

                {/* Subheadline Paragraph */}
                <p
                  className="hero-subheadline animate-in"
                  style={{ "--enter-delay": "220ms" }}
                >
                  We design, build, and scale AI products — from LLM-powered
                  automation to production SaaS platforms.
                </p>

                {/* CTA Button Row */}
                <div
                  className="hero-buttons animate-in"
                  style={{ "--enter-delay": "360ms" }}
                >
                  <button
                    onClick={() => navigate("/about/contact")}
                    onPointerMove={onMagneticMove}
                    onPointerLeave={onMagneticLeave}
                    className="btn btn-primary-custom hero-magnetic glow-border"
                  >
                    Book a Consultation
                  </button>
                  <button
                    className="btn btn-outline-custom hero-magnetic"
                    onClick={() => navigate("/products/unibuilder")}
                    onPointerMove={onMagneticMove}
                    onPointerLeave={onMagneticLeave}
                  >
                    Explore Our Products
                  </button>
                </div>

                {/* Bottom Row: link + trust strip */}
                <div
                  className="hero-bottom-row animate-in"
                  style={{ "--enter-delay": "500ms" }}
                >
                  <button
                    type="button"
                    className="hero-link"
                    onClick={() => navigate("/services/ai-applications")}
                  >
                    See what we build →
                  </button>
                  <span className="hero-sep" aria-hidden="true">
                    •
                  </span>
                  <div className="hero-trust">
                    <div className="avatar-stack" aria-hidden="true">
                      {["RK", "SP", "MV", "AS"].map((initials, i) => (
                        <div
                          key={initials}
                          className={`avatar-item av-${i + 1}`}
                          style={{ "--stagger-index": i }}
                        >
                          <span>{initials}</span>
                        </div>
                      ))}
                    </div>
                    <p className="trust-text">
                      Trusted by{" "}
                      <strong className="trust-count">{founderCount}</strong>{" "}
                      founders
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Visual */}
            <div className="col-lg-6 col-12">
              <div className="hero-mockup">
                {/* Rotating orbit rings behind the subject */}
                <div className="hero-orbit" aria-hidden="true">
                  <span className="hero-orbit-ring hero-orbit-ring--1" />
                  <span className="hero-orbit-ring hero-orbit-ring--2" />
                </div>

                <div className="mockup-frame float">
                  <img
                    src={Section}
                    alt="AI neural network visualisation"
                    className="mockup-img"
                    width="720"
                    height="540"
                    fetchPriority="high"
                  />
                </div>

                {/* Floating live-metric badges */}
                <div
                  className="hero-badge hero-badge--tl animate-in"
                  style={{ "--enter-delay": "700ms" }}
                >
                  <span className="hero-badge-dot pulse-dot" aria-hidden="true" />
                  <span className="hero-badge-label">Models deployed</span>
                  <span className="hero-badge-value">24/7</span>
                </div>

                <div
                  className="hero-badge hero-badge--br animate-in"
                  style={{ "--enter-delay": "860ms" }}
                >
                  <span className="hero-badge-label">Inference latency</span>
                  <span className="hero-badge-value">&lt; 200ms</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
